/**
 * Shared query helpers. Every read goes to the server (the client's default fetchPolicy is
 * 'network-only', see src/util/apolloClient.ts); the fetchPolicy is still spelled out here so
 * that a query never depends on that default.
 */
import type { DocumentNode } from 'graphql'
import { AliyahType, AnnotationSetType, ParashahType, Query, SentenceGroupType, SentenceType, TorahColumnOverlayType } from 'types/types'

import { COLUMN_ANALYSIS_MAX_WAIT_MS, COLUMN_ANALYSIS_POLL_INTERVAL_MS } from '@/constants/appConstants'
import AliyahSentencesQuery from '@/queries/AliyahSentencesQuery.graphql'
import AliyotQuery from '@/queries/AliyotQuery.graphql'
import BarMitzvahAnnotationsQuery from '@/queries/BarMitzvahAnnotationsQuery.graphql'
import ParshiyotQuery from '@/queries/ParshiyotQuery.graphql'
import SentenceGroupSentencesQuery from '@/queries/SentenceGroupSentencesQuery.graphql'
import SentenceGroupsLightQuery from '@/queries/SentenceGroupsLightQuery.graphql'
import SentencesBySefariaRangeQuery from '@/queries/SentencesBySefariaRangeQuery.graphql'
import TorahColumnOverlaysQuery from '@/queries/TorahColumnOverlaysQuery.graphql'
import UserIsAuthenticatedQuery from '@/queries/UserIsAuthenticatedQuery.graphql'
import { useApollo } from '@/util/apolloClient'

const { apolloClient } = useApollo()

/** The element type of the list field `K` of `Query`. */
type QueryListItem<K extends keyof Query> = NonNullable<Extract<NonNullable<Query[K]>, Array<unknown>>[number]>

/** Run a query and return the field `dataKey` of its result. Rejects on a network or server error. */
export async function queryField<K extends keyof Query>(
  dataKey: K,
  query: DocumentNode,
  variables?: Record<string, unknown>,
  errorPolicy?: 'all' | 'ignore' | 'none',
): Promise<Query[K] | null | undefined> {
  const response = await apolloClient.query<Pick<Query, K>>({ errorPolicy, fetchPolicy: 'network-only', query, variables })
  return response.data?.[dataKey]
}

/** Like queryField for a list field: null entries are dropped and a missing list becomes []. */
export async function queryList<K extends keyof Query>(
  dataKey: K,
  query: DocumentNode,
  variables?: Record<string, unknown>,
): Promise<Array<QueryListItem<K>>> {
  const list = (await queryField(dataKey, query, variables)) as Array<QueryListItem<K> | null> | null | undefined
  return (list?.filter(Boolean) ?? []) as Array<QueryListItem<K>>
}

/**
 * Fetch sentences for a sentence group. Shared between student and teacher stores.
 * Returns the commit payload ({ sentenceGroup, sentences }).
 */
export async function querySentenceGroupSentences(
  sentenceGroup: SentenceGroupType,
): Promise<{ sentenceGroup: SentenceGroupType; sentences: SentenceType[] }> {
  return {
    sentenceGroup,
    sentences: await queryList('sentenceGroupSentences', SentenceGroupSentencesQuery, { sentenceGroup: sentenceGroup.reading.id }),
  }
}

/** Every parashah, unsorted. */
export function queryParshiyot(): Promise<ParashahType[]> {
  return queryList('parshiyot', ParshiyotQuery)
}

/** The aliyot of one parashah (not only the current user's). */
export function queryAliyot(parashahId: string | number): Promise<AliyahType[]> {
  return queryList('aliyot', AliyotQuery, { mine: false, parashah: parashahId })
}

/** All sentence groups, without their sentences. */
export function querySentenceGroupsLight(): Promise<SentenceGroupType[]> {
  return queryList('sentenceGroups', SentenceGroupsLightQuery, { mine: false })
}

/** The annotation sets of one student's bar mitzvah. */
export function queryBarMitzvahAnnotations(username: string): Promise<AnnotationSetType[]> {
  return queryList('barMitzvahAnnotations', BarMitzvahAnnotationsQuery, { username })
}

/** The sentences of one aliyah. */
export function queryAliyahSentences(aliyahId: string | number): Promise<SentenceType[]> {
  return queryList('aliyahSentences', AliyahSentencesQuery, { aliyah: aliyahId })
}

/** Whether the server still considers this session logged in. Rejects when it cannot tell. */
export async function queryUserIsAuthenticated(): Promise<boolean> {
  const status = await queryField('userIsAuthenticated', UserIsAuthenticatedQuery, undefined, 'all')
  return !!status?.ok
}

/** Fetch the sentences from `firstSefariaId` through `lastSefariaId`. */
export function querySentencesBySefariaRange(firstSefariaId: string, lastSefariaId: string): Promise<SentenceType[]> {
  return queryList('sentencesBySefariaRange', SentencesBySefariaRangeQuery, { firstSefariaId, lastSefariaId })
}

/** Fetch the Torah column overlays of one student. */
export function queryTorahColumnOverlays(username: string): Promise<TorahColumnOverlayType[]> {
  return queryList('torahColumnOverlays', TorahColumnOverlaysQuery, { barMitzvah: username })
}

/**
 * Poll a student's overlay until the server has finished analyzing its photo (processingSuccess is no longer null).
 * Resolves to the finished overlay, or to null if it was deleted meanwhile or the wait ran out.
 */
export async function waitForColumnAnalysis(
  username: string,
  overlayId: string,
  { intervalMs = COLUMN_ANALYSIS_POLL_INTERVAL_MS, maxWaitMs = COLUMN_ANALYSIS_MAX_WAIT_MS } = {},
): Promise<TorahColumnOverlayType | null> {
  const deadline = Date.now() + maxWaitMs
  while (Date.now() < deadline) {
    const overlay = (await queryTorahColumnOverlays(username)).find((candidate) => candidate.id === overlayId)
    if (!overlay) {
      return null
    }
    if (overlay.processingSuccess != null) {
      return overlay
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
  return null
}
